import { FC, useMemo, useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import Button from 'modules/_shared/components/Buttons';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import menuTypes from 'constants/menuTypes';

import { TradingOfferOperation } from 'types/TradingOfferOperation';
import { Partner } from 'types';
import { State } from 'redux/initialState';
import { useEquityMarket } from '../../contexts/EquityMarketContext';
import EquityMarketAdminPanelOperationsTable from '../../components/EquityMarketAdminPanelOperationsTable';

import '../../styles/Trading.scss';

const PENDING_FOR_BUYER_STATUSES = ['KYC_REVIEW', 'PAYMENT_PENDING'];

const getPartyId = (party: string | Partial<Partner>): string | null => {
  if (!party) return null;
  if (typeof party === 'string') return party;
  return (party as Partner)?._id ?? null;
};

const isBuyerOperation = (
  operation: TradingOfferOperation,
  currentPartnerId: string
): boolean => {
  const buyerId = getPartyId(operation.buyer);
  return buyerId === currentPartnerId;
};

const isPendingForBuyer = (op: TradingOfferOperation): boolean =>
  PENDING_FOR_BUYER_STATUSES.includes(op.status);

const hasPendingOperationsForBuyer = (
  operations: TradingOfferOperation[],
  buyerPartnerId: string | null
): boolean => {
  if (!buyerPartnerId) return false;
  return operations.some(
    (op) => isBuyerOperation(op, buyerPartnerId) && isPendingForBuyer(op)
  );
};

const TradingOffersMyOperations: FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { data } = useEquityMarket();
  const hasShownPendingAlert = useRef(false);

  const queryParams = new URLSearchParams(location.search);
  const partnerId = queryParams.get('partnerId');
  const partnerQuery = partnerId ? `?partnerId=${partnerId}` : '';

  const allOperations = useSelector(
    ({ tradingOfferOperations }: State) =>
      (tradingOfferOperations ?? []) as TradingOfferOperation[]
  );

  useGetMenu(menuTypes.NULL);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: data.societyName, to: `/sociedad/${data.societyId}` },
      {
        label: t('EquityMarket'),
        to: `/mercado-de-acciones/${data.societyId}${partnerQuery}`,
      },
      { label: t('MyOperations'), isActive: true },
    ],
    [t, data.societyName, data.societyId, partnerQuery]
  );

  const hasPendingOperations = useMemo(
    () => hasPendingOperationsForBuyer(allOperations, partnerId),
    [allOperations, partnerId]
  );

  useEffect(() => {
    if (hasPendingOperations && !hasShownPendingAlert.current) {
      hasShownPendingAlert.current = true;
      dispatch(addAlert(alertBodyTypes.MY_OPERATIONS_PENDING_ATTENTION));
    }
  }, [hasPendingOperations, dispatch]);

  const handleGoToOperationDetail = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-oferta/${data.societyId}/${operationId}${partnerQuery}`
      );
    },
    [history, data.societyId, partnerQuery]
  );

  const handleGoToMarket = useCallback(() => {
    history.push(`/mercado-de-acciones/${data.societyId}${partnerQuery}`);
  }, [history, data.societyId, partnerQuery]);

  const pageActions = [
    <GoBackButton key="go-back" icon="ni-arrow-left" />,
    <Button key="go-to-market" onClick={handleGoToMarket}>
      <span>{t('GoToMarket')}</span>
    </Button>,
  ];

  if (data.isLoading) return <CustomLoading />;

  return (
    <Page id="trading-my-operations-page" className="pb-5">
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <PageListHeader
        subTitle={t('MyOperations')}
        description={t('MyOperationsListDescription')}
        actions={pageActions}
      />

      <div className="nk-block nk-block-lg">
        <div className="card card-bordered">
          <div className="card-inner">
            <h6 className="card-title mb-3">
              <em className="icon ni ni-list text-primary mr-2" />
              {t('OperationsList')}
            </h6>

            <p className="text-soft fs-12px mb-3">
              <em className="icon ni ni-info text-soft mr-1" />
              {t('MyOperationsListHint')}
            </p>
          </div>

          <div className="card-inner p-0">
            <EquityMarketAdminPanelOperationsTable
              operations={allOperations}
              onViewOperationDetail={handleGoToOperationDetail}
              variant="partner"
              partnerId={partnerId}
              embedded
            />
          </div>

          <div className="card-inner bg-lighter border-top">
            <button
              type="button"
              className="btn btn-outline-primary mr-2"
              onClick={() =>
                history.push(
                  `/mercado-de-acciones/${data.societyId}${partnerQuery}`
                )
              }
            >
              {t('MyOperationsSummary')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGoToMarket}
            >
              {t('GoToMarket')}
            </button>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default TradingOffersMyOperations;
